import { ScrollArea } from '@/components/ui/scroll-area';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Plus, Minus, Columns2, Rows2, Trash2, ToyBrick, DoorOpen, LayoutGrid as LayoutGridIcon, Box as BoxIcon } from 'lucide-react';
import { MATERIALS, COLORS } from '@/lib/furnitureData';
import { CELL_FILLS, cellKey } from '@/lib/parametric/schema';
import {
  computeColRects, computeRowRects,
  splitColumn, splitRow, removeColumn, removeRow,
} from '@/lib/parametric/grid';

const FILL_OPTIONS = [
  { id: CELL_FILLS.empty,     label: 'Пусто' },
  { id: CELL_FILLS.shelf,     label: 'Полка' },
  { id: CELL_FILLS.shelves,   label: 'Несколько полок' },
  { id: CELL_FILLS.rod,       label: 'Штанга + полка' },
  { id: CELL_FILLS.door,      label: 'Дверь распашная' },
  { id: CELL_FILLS.flap_door, label: 'Дверь откидная' },
  { id: CELL_FILLS.drawer,    label: 'Ящик' },
  { id: CELL_FILLS.drawers,   label: 'Несколько ящиков' },
];

export default function ParametricPanel({
  source, selectedCellKey, onSelectCell, onChange,
  doorsOpen, onToggleDoors, techView, onToggleTechView,
}) {
  if (!source) {
    return (
      <div className="w-80 bg-[#FAFAFA] border-l border-[#E0DDD8] flex flex-col shrink-0">
        <div className="p-4 border-b border-[#E0DDD8]">
          <h3 className="text-xs font-semibold text-[#5C5C5C] uppercase tracking-wider">Параметры</h3>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <p className="text-xs text-[#5C5C5C] text-center">
            Включите параметрический режим в шапке, чтобы править корпус и ячейки.
          </p>
        </div>
      </div>
    );
  }

  const isV2 = source.kind === 'parametric-v2';
  const body = isV2
    ? {
      width: Number(source.dimensions?.w || 120),
      height: Number(source.dimensions?.h || 200),
      depth: Number(source.dimensions?.d || 60),
      material: source.attributes?.body_material || 'mdf',
      color: source.attributes?.body_color || 'light_oak',
    }
    : source.body;
  const grid = source.grid || { cols: [{ frac: 1 }], rows: [{ frac: 1 }] };
  const cells = source.cells || { '0,0': { fill: CELL_FILLS.empty } };
  const colRects = computeColRects(body, grid);
  const rowRects = computeRowRects(body, grid);

  const setBody = (patch) => {
    if (isV2) {
      onChange({
        ...source,
        dimensions: {
          ...source.dimensions,
          w: patch.width ?? body.width,
          h: patch.height ?? body.height,
          d: patch.depth ?? body.depth,
        },
        attributes: {
          ...source.attributes,
          body_material: patch.material ?? body.material,
          body_color: patch.color ?? body.color,
        },
      }, 'Корпус изменён');
      return;
    }
    onChange({ ...source, body: { ...body, ...patch } }, 'Корпус изменён');
  };

  const doSplitCol = (c) => {
    if (isV2) return;
    const r = splitColumn(grid, cells, c);
    onChange({ ...source, grid: { ...grid, cols: r.cols }, cells: r.cells }, 'Колонка разделена');
  };
  const doSplitRow = (r) => {
    if (isV2) return;
    const nx = splitRow(grid, cells, r);
    onChange({ ...source, grid: { ...grid, rows: nx.rows }, cells: nx.cells }, 'Ряд разделён');
  };
  const doRemoveCol = (c) => {
    if (isV2) return;
    const r = removeColumn(grid, cells, c);
    onChange({ ...source, grid: { ...grid, cols: r.cols }, cells: r.cells }, 'Колонка удалена');
  };
  const doRemoveRow = (r) => {
    if (isV2) return;
    const nx = removeRow(grid, cells, r);
    onChange({ ...source, grid: { ...grid, rows: nx.rows }, cells: nx.cells }, 'Ряд удалён');
  };

  const selectedCell = selectedCellKey ? cells[selectedCellKey] : null;
  const [sc, sr] = selectedCellKey ? selectedCellKey.split(',').map(Number) : [null, null];
  const selectedRect = selectedCellKey
    ? { w: colRects[sc]?.w || 0, h: rowRects[sr]?.h || 0 }
    : null;

  const setCell = (patch) => {
    if (isV2) return;
    if (!selectedCellKey) return;
    const nextCells = { ...cells, [selectedCellKey]: { ...selectedCell, ...patch } };
    onChange({ ...source, cells: nextCells }, 'Ячейка изменена');
  };

  return (
    <div className="w-80 bg-[#FAFAFA] border-l border-[#E0DDD8] flex flex-col shrink-0" data-testid="parametric-panel">
      <div className="p-4 border-b border-[#E0DDD8] flex items-center justify-between">
        <h3 className="text-xs font-semibold text-[#5C5C5C] uppercase tracking-wider">Параметры шкафа</h3>
        <div className="flex gap-1">
          <Button variant={techView ? 'default' : 'outline'} size="sm" className="h-7 px-2" onClick={onToggleTechView}>
            <LayoutGridIcon className="w-3.5 h-3.5 mr-1" /> Разм.
          </Button>
          <Button variant={doorsOpen ? 'default' : 'outline'} size="sm" className="h-7 px-2" onClick={onToggleDoors}>
            <DoorOpen className="w-3.5 h-3.5 mr-1" /> Открыть
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-5">
          {/* Корпус */}
          <section className="space-y-3">
            <div className="text-[11px] font-semibold text-[#5C5C5C] uppercase tracking-wider flex items-center gap-1">
              <BoxIcon className="w-3.5 h-3.5" /> Корпус
            </div>
            <NumberSlider label="Ширина, см"  value={body.width}  min={40} max={300} onChange={v => setBody({ width: v })} />
            <NumberSlider label="Высота, см"  value={body.height} min={40} max={260} onChange={v => setBody({ height: v })} />
            <NumberSlider label="Глубина, см" value={body.depth}  min={20} max={80}  onChange={v => setBody({ depth: v })} />

            <div>
              <Label className="text-xs">Материал</Label>
              <Select value={body.material} onValueChange={v => setBody({ material: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MATERIALS.map(m => <SelectItem key={m.id} value={m.id}>{m.nameRu}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Цвет</Label>
              <div className="grid grid-cols-6 gap-1 mt-1">
                {COLORS.map(c => (
                  <button key={c.id} title={c.nameRu}
                    onClick={() => setBody({ color: c.id, color_hex: c.hex })}
                    className={`h-7 rounded border ${body.color === c.id ? 'ring-2 ring-[#8B7A5F]' : 'border-[#DDD]'}`}
                    style={{ background: c.hex }} />
                ))}
              </div>
            </div>
          </section>

          <Separator />

          {/* Сетка */}
          <section className="space-y-3">
            <div className="text-[11px] font-semibold text-[#5C5C5C] uppercase tracking-wider flex items-center gap-1">
              <LayoutGridIcon className="w-3.5 h-3.5" /> Сетка ({colRects.length} × {rowRects.length})
            </div>

            <div>
              <div className="text-xs text-[#5C5C5C] mb-1">Колонки</div>
              <div className="space-y-1">
                {colRects.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="w-14">#{i + 1}: {Math.round(r.w * 10)} мм</span>
                    <Button size="icon" variant="outline" className="h-6 w-6" title="Разделить колонку пополам"
                      disabled={isV2}
                      onClick={() => doSplitCol(i)}>
                      <Columns2 className="w-3 h-3" />
                    </Button>
                    <Button size="icon" variant="outline" className="h-6 w-6" title="Удалить колонку"
                      disabled={isV2 || colRects.length <= 1}
                      onClick={() => doRemoveCol(i)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs text-[#5C5C5C] mb-1">Ряды (сверху вниз)</div>
              <div className="space-y-1">
                {rowRects.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="w-14">#{i + 1}: {Math.round(r.h * 10)} мм</span>
                    <Button size="icon" variant="outline" className="h-6 w-6" title="Разделить ряд пополам"
                      disabled={isV2}
                      onClick={() => doSplitRow(i)}>
                      <Rows2 className="w-3 h-3" />
                    </Button>
                    <Button size="icon" variant="outline" className="h-6 w-6" title="Удалить ряд"
                      disabled={isV2 || rowRects.length <= 1}
                      onClick={() => doRemoveRow(i)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <Separator />

          {/* Ячейки — матрица для выбора */}
          <section className="space-y-3">
            <div className="text-[11px] font-semibold text-[#5C5C5C] uppercase tracking-wider flex items-center gap-1">
              <ToyBrick className="w-3.5 h-3.5" /> Ячейки
            </div>
            <div className="text-[10px] text-[#5C5C5C]">Клик — выбрать ячейку, справа её настройки.</div>
            <div className="border border-[#DDD] bg-white p-2 rounded">
              <div className="grid gap-0.5" style={{
                gridTemplateColumns: `repeat(${colRects.length}, 1fr)`,
                gridTemplateRows: `repeat(${rowRects.length}, 1fr)`,
                aspectRatio: `${body.width} / ${body.height}`,
              }}>
                {rowRects.map((_, r) =>
                  colRects.map((_, c) => {
                    const k = cellKey(c, r);
                    const cell = cells[k] || { fill: 'empty' };
                    const isSel = k === selectedCellKey;
                    return (
                      <button key={k} onClick={() => onSelectCell(k)}
                        className={`text-[9px] rounded flex items-center justify-center border transition ${
                          isSel ? 'border-[#8B7A5F] ring-2 ring-[#8B7A5F] bg-[#F4EFE4]' : 'border-[#DDD] bg-[#FAFAFA] hover:bg-[#F4EFE4]'
                        }`}
                        style={{ minHeight: 28 }}>
                        {cell.fill === 'empty' ? '·' : fillShort(cell.fill)}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {selectedCell && (
              <div className="space-y-2 p-3 bg-white rounded border border-[#DDD]">
                <div className="text-xs font-semibold">Ячейка {sc + 1}×{sr + 1}</div>
                <div className="text-[11px] text-[#5C5C5C]">
                  {Math.round(selectedRect.w * 10)} × {Math.round(selectedRect.h * 10)} мм
                </div>
                <div>
                  <Label className="text-xs">Наполнение</Label>
                  <Select value={selectedCell.fill} onValueChange={v => setCell({ fill: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FILL_OPTIONS.map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {(selectedCell.fill === 'shelves' || selectedCell.fill === 'drawers') && (
                  <NumberSlider label="Количество" value={selectedCell.count || 3} min={1} max={6}
                    onChange={v => setCell({ count: v })} />
                )}

                {(selectedCell.fill === 'door' || selectedCell.fill === 'drawer' || selectedCell.fill === 'flap_door') && (
                  <>
                    <div>
                      <Label className="text-xs">Установка</Label>
                      <Select value={selectedCell.overlay === false ? 'inset' : 'overlay'}
                        onValueChange={v => setCell({ overlay: v === 'overlay' })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="overlay">Накладная</SelectItem>
                          <SelectItem value="inset">Вкладная</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedCell.fill === 'door' && (
                      <div>
                        <Label className="text-xs">Сторона навеса</Label>
                        <Select value={selectedCell.hinge || 'L'} onValueChange={v => setCell({ hinge: v })}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="L">Слева</SelectItem>
                            <SelectItem value="R">Справа</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </section>
        </div>
      </ScrollArea>
    </div>
  );
}

function fillShort(f) {
  switch (f) {
    case 'shelf': case 'shelves': return 'полка';
    case 'rod': return 'штанга';
    case 'door': return 'дверь';
    case 'flap_door': return 'откид.';
    case 'drawer': case 'drawers': return 'ящик';
    default: return '';
  }
}

function NumberSlider({ label, value, min, max, step = 1, onChange }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <Label className="text-xs">{label}</Label>
        <span className="font-mono text-[11px]">{Math.round(value)}</span>
      </div>
      <Slider value={[value]} min={min} max={max} step={step}
        onValueChange={([v]) => onChange(v)} />
    </div>
  );
}
